import { AxiosError, AxiosResponse, isAxiosError } from 'axios';
import { logDiagnostic } from './logDiagnostic';

/**
 * Browser path prefixes the web server (Vite in local dev, nginx in deployed envs) must proxy to a
 * backend, with the env var that carries the target. Longest prefix first: `/list-api` also
 * contains `/api`.
 */
const PROXY_ROUTES = [
  {
    prefix: '/list-api',
    service: 'Org / lists API',
    proxyTargetEnv: 'API_LIST_PROXY_TARGET',
  },
  {
    prefix: '/dms-api',
    service: 'DMS API',
    proxyTargetEnv: 'DMS_API_PROXY_TARGET',
  },
  {
    prefix: '/admin-api',
    service: 'Admin API',
    proxyTargetEnv: 'ADMIN_API_PROXY_TARGET',
  },
  {
    prefix: '/api',
    service: 'Anova API',
    proxyTargetEnv: 'API_PROXY_TARGET',
  },
] as const;

type ProxyRoute = (typeof PROXY_ROUTES)[number];

type RequestLike = {
  readonly baseURL?: string;
  readonly url?: string;
  readonly method?: string;
};

export function getRequestUrl(config?: RequestLike): string {
  const base = config?.baseURL ?? '';
  const url = config?.url ?? '';

  if (!url || /^https?:\/\//i.test(url)) {
    return url || base;
  }

  return `${base.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
}

function getRequestLabel(config?: RequestLike): string {
  const method = (config?.method ?? 'get').toUpperCase();
  return `${method} ${getRequestUrl(config)}`;
}

export function findProxyRoute(url: string): ProxyRoute | undefined {
  const path = url.replace(/^https?:\/\/[^/]+/i, '');
  return PROXY_ROUTES.find((route) => path.startsWith(route.prefix));
}

/**
 * True when an API answered with an HTML document. This is what a misrouted API path looks like:
 * the web server falls back to the SPA `index.html` and answers 200, so the caller sees a
 * successful request with no usable payload.
 */
export function isHtmlPayload(response: {
  readonly headers?: unknown;
  readonly data?: unknown;
}): boolean {
  const headers = response.headers as
    | { get?: (name: string) => unknown; ['content-type']?: unknown }
    | undefined;
  const rawContentType =
    typeof headers?.get === 'function'
      ? headers.get('content-type')
      : headers?.['content-type'];

  if (typeof rawContentType === 'string') {
    return rawContentType.toLowerCase().includes('text/html');
  }

  return (
    typeof response.data === 'string' &&
    /^\s*<(!doctype html|html)/i.test(response.data)
  );
}

function routingFixHints(route: ProxyRoute | undefined): string[] {
  if (!route) {
    return [
      'Check the base URL used for this request in window.anovaAppConfig.',
    ];
  }

  return [
    `Local dev: set ${route.proxyTargetEnv} in .env.local and restart "npm start" (Vite proxies ${route.prefix} to it).`,
    `Deployed env: set ${route.proxyTargetEnv} on the web container so the nginx "location ${route.prefix}" block proxies it; start.sh prints the rendered config.`,
    `Verify with: curl -i <site>${route.prefix}/... — a JSON content-type means the route is fixed.`,
  ];
}

/**
 * Reports an API answer that reached the web server but never reached the API itself.
 */
export function reportApiResponse(response: AxiosResponse): void {
  if (!isHtmlPayload(response)) {
    return;
  }

  const url = getRequestUrl(response.config);
  const route = findProxyRoute(url);

  logDiagnostic({
    key: `html-response:${route?.prefix ?? url}`,
    title: `${
      route?.service ?? 'An API'
    } is not routed to a backend — it answered with the web app instead of JSON`,
    details: {
      request: getRequestLabel(response.config),
      status: `${response.status} ${response.statusText ?? ''}`.trim(),
      received: 'text/html (the SPA index.html), so response data is unusable',
    },
    fix: routingFixHints(route),
  });
}

/**
 * Reports a request that failed before or instead of producing an API payload.
 */
export function reportApiError(error: unknown): void {
  if (!isAxiosError(error) || error.code === 'ERR_CANCELED') {
    return;
  }

  const axiosError: AxiosError = error;
  const url = getRequestUrl(axiosError.config ?? undefined);
  const route = findProxyRoute(url);
  const status = axiosError.response?.status;

  if (axiosError.response) {
    reportApiResponse(axiosError.response);
  }

  if (status === undefined) {
    logDiagnostic({
      key: `unreachable:${route?.prefix ?? url}`,
      title: `${route?.service ?? 'An API'} is unreachable`,
      details: {
        request: getRequestLabel(axiosError.config ?? undefined),
        error: `${axiosError.code ?? 'no response'} — ${axiosError.message}`,
      },
      fix: [
        'Check that the backend is running and reachable from this machine (VPN, firewall, self-signed certificate).',
        ...routingFixHints(route),
      ],
    });
    return;
  }

  if (status === 502 || status === 503 || status === 504) {
    logDiagnostic({
      key: `gateway-error:${route?.prefix ?? url}:${status}`,
      title: `${
        route?.service ?? 'An API'
      } is routed but the backend behind it is down (${status})`,
      details: {
        request: getRequestLabel(axiosError.config ?? undefined),
        status: String(status),
      },
      fix: [
        route
          ? `Check the service ${route.proxyTargetEnv} points at — the proxy reached it but got no valid answer.`
          : 'Check the upstream service for this request.',
        'Retry after the backend finished restarting; deployments answer 502/503 while warming up.',
      ],
    });
  }
}
